import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { AppShell } from "./AppShell";
import { CallModelStack } from "./CallModelStack";
import { CallSummary } from "./CallSummary";
import { EmptyState, LoadingState } from "./components/states";
import { API_URL, fmtPhone, fmtTime, prettyJson, relTime, suppressNavOnSelection } from "./lib/api";

type CallSummary = {
  id: number;
  direction: "inbound" | "outbound" | "copilot";
  from_number: string;
  to_number: string;
  started_at: string;
  ended_at: string | null;
  outcome: string | null;
  is_test: number;
  customer_id: number | null;
  first_name: string | null;
  last_name: string | null;
  customer_phone: string | null;
};

const SHOW_TESTS_KEY = "outbound:showTests";

export function Outbound({ callId }: { callId?: number }) {
  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [showTests, setShowTests] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_TESTS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [, setLocation] = useLocation();

  // is_test=0 keeps the production view clean by default; the "Show test
  // runs" toggle drops the filter so synthetic /scenarios dials become
  // visible (with a TEST badge, see OutboundRow). Setting persists in
  // localStorage so the operator's preference sticks.
  useEffect(() => {
    let cancelled = false;
    const filterParam = showTests ? "" : "&is_test=0";
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/calls?limit=100${filterParam}`);
        const d = (await r.json()) as { calls: CallSummary[] };
        if (!cancelled) setCalls(d.calls.filter((c) => c.direction === "outbound"));
      } catch {
        /* swallow — SSE will catch up */
      }
    })();
    const streamUrl = showTests
      ? `${API_URL}/api/calls/stream`
      : `${API_URL}/api/calls/stream?is_test=0`;
    const es = new EventSource(streamUrl);
    es.addEventListener("calls", (ev) => {
      const d = JSON.parse((ev as MessageEvent).data) as { calls: CallSummary[] };
      setCalls(d.calls.filter((c) => c.direction === "outbound"));
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [showTests]);

  function toggleShowTests(next: boolean) {
    setShowTests(next);
    try {
      localStorage.setItem(SHOW_TESTS_KEY, next ? "1" : "0");
    } catch {
      /* private mode — toggle still works for the session */
    }
  }

  // Pin live calls (ended_at IS NULL) above completed ones — mirrors Inbound.
  const { live, recent } = useMemo(() => {
    const live: CallSummary[] = [];
    const recent: CallSummary[] = [];
    for (const c of calls) (c.ended_at === null ? live : recent).push(c);
    return { live, recent };
  }, [calls]);

  useEffect(() => {
    if (callId !== undefined) return;
    const target = live[0] ?? recent[0];
    if (target) setLocation(`/outbound/${target.id}`, { replace: true });
  }, [callId, live, recent, setLocation]);

  return (
    <AppShell>
      <div id="outbound-root" className="inbound">
        <OutboundList
          live={live}
          recent={recent}
          selectedId={callId}
          showTests={showTests}
          onToggleShowTests={toggleShowTests}
        />
        {callId !== undefined ? (
          <OutboundDetail callId={callId} />
        ) : (
          <div id="outbound-empty" className="inbound-empty">
            <EmptyState
              id="outbound-empty-state"
              title="No outbound calls yet"
              hint="Outbound calls run from Settings → Outbound. Once the AI starts dialling, calls show up here."
            />
          </div>
        )}
      </div>
    </AppShell>
  );
}

function OutboundList({
  live,
  recent,
  selectedId,
  showTests,
  onToggleShowTests,
}: {
  live: CallSummary[];
  recent: CallSummary[];
  selectedId: number | undefined;
  showTests: boolean;
  onToggleShowTests: (next: boolean) => void;
}) {
  const total = live.length + recent.length;
  return (
    <aside id="outbound-call-list" className="call-list">
      <header className="pane-header pane-header-with-toggle">
        <span>OUTBOUND CALLS</span>
        <label className="show-tests-toggle" title="Include /scenarios test runs">
          <input
            id="outbound-show-tests-toggle"
            type="checkbox"
            checked={showTests}
            onChange={(e) => onToggleShowTests(e.target.checked)}
          />
          <span>Show test runs</span>
        </label>
      </header>

      {total === 0 && <div id="outbound-call-list-empty" className="call-row-empty">No calls.</div>}

      {live.length > 0 && (
        <>
          <div className="call-section-label">
            <span className="pulse" /> Live ({live.length})
          </div>
          {live.map((c) => (
            <OutboundRow key={c.id} call={c} selectedId={selectedId} />
          ))}
        </>
      )}

      {recent.length > 0 && (
        <>
          {live.length > 0 && (
            <div className="call-section-label">Recent ({recent.length})</div>
          )}
          {recent.map((c) => (
            <OutboundRow key={c.id} call={c} selectedId={selectedId} />
          ))}
        </>
      )}
    </aside>
  );
}

function OutboundRow({
  call,
  selectedId,
}: {
  call: CallSummary;
  selectedId: number | undefined;
}) {
  const active = call.ended_at === null;
  const name =
    [call.first_name, call.last_name].filter(Boolean).join(" ") ||
    call.customer_phone ||
    call.to_number;
  return (
    <Link
      id={`outbound-call-row-${call.id}`}
      href={`/outbound/${call.id}`}
      className={`call-row ${selectedId === call.id ? "is-selected" : ""}`}
      onClick={suppressNavOnSelection}
    >
      <div className="call-row-top">
        <span className={`call-status ${active ? "is-active" : "is-done"}`}>
          {active ? <span className="pulse" /> : null}
          {active ? "Live" : call.outcome ?? "ended"}
        </span>
        {call.is_test === 1 && (
          <span className="call-row-test-badge" title="from /scenarios test run">
            TEST
          </span>
        )}
        <span className="call-row-time">{relTime(call.started_at)}</span>
      </div>
      <div className="call-row-name">{name}</div>
      <div className="call-row-num">{fmtPhone(call.to_number)}</div>
    </Link>
  );
}

// --- detail pane: same shape as Inbound's detail (transcript / tools / logs)
type CallDetail = CallSummary & {
  recording_url: string | null;
  transcript: string | null;
  notes: string | null;
  /** JSON-serialised provider/model snapshot the bot wrote at call start. */
  model_snapshot: string | null;
};
type Transcript = {
  id: number;
  ts: string;
  role: "customer" | "agent" | "assistant" | "system";
  text: string;
};
type ToolCall = {
  id: number;
  ts: string;
  name: string;
  arguments: string | null;
  result: string | null;
  status: "pending" | "completed" | "errored";
  duration_ms: number | null;
};

function OutboundDetail({ callId }: { callId: number }) {
  const [call, setCall] = useState<CallDetail | null>(null);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [toolCalls, setToolCalls] = useState<ToolCall[]>([]);
  const [tab, setTab] = useState<"summary" | "transcript" | "tools">("summary");

  useEffect(() => {
    setCall(null);
    setTranscripts([]);
    setToolCalls([]);
    let cancelled = false;
    (async () => {
      const [callRes, tRes, tcRes] = await Promise.all([
        fetch(`${API_URL}/api/calls/${callId}`),
        fetch(`${API_URL}/api/calls/${callId}/transcripts`),
        fetch(`${API_URL}/api/calls/${callId}/tool-calls`),
      ]);
      if (cancelled) return;
      if (callRes.ok) setCall((await callRes.json()) as CallDetail);
      if (tRes.ok) setTranscripts((await tRes.json()).transcripts);
      if (tcRes.ok) setToolCalls((await tcRes.json()).tool_calls);
    })();

    const es = new EventSource(`${API_URL}/api/calls/${callId}/stream`);
    es.addEventListener("transcript", (ev) => {
      const row = JSON.parse((ev as MessageEvent).data) as Transcript;
      setTranscripts((cur) => (cur.some((x) => x.id === row.id) ? cur : [...cur, row]));
    });
    es.addEventListener("tool_call", (ev) => {
      const row = JSON.parse((ev as MessageEvent).data) as ToolCall;
      setToolCalls((cur) => {
        const i = cur.findIndex((x) => x.id === row.id);
        if (i === -1) return [...cur, row];
        const copy = cur.slice();
        copy[i] = row;
        return copy;
      });
    });
    es.addEventListener("call", (ev) => {
      const row = JSON.parse((ev as MessageEvent).data) as Partial<CallDetail>;
      setCall((cur) => (cur ? { ...cur, ...row } : cur));
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [callId]);

  if (!call) {
    return (
      <section id="outbound-call-detail" className="call-detail">
        <LoadingState id="outbound-call-detail-loading" label="Loading call…" />
      </section>
    );
  }
  const active = call.ended_at === null;
  const name =
    [call.first_name, call.last_name].filter(Boolean).join(" ") ||
    call.customer_phone ||
    call.to_number;

  return (
    <section id="outbound-call-detail" className="call-detail">
      <header id="outbound-detail-header" className="detail-header">
        <div>
          <div className="detail-name">{name}</div>
          <div className="detail-sub">
            {fmtPhone(call.from_number)} → {fmtPhone(call.to_number)} · outbound ·{" "}
            <span className={active ? "tag-live" : "tag-done"}>
              {active ? "Live" : call.outcome ?? "ended"}
            </span>
          </div>
        </div>
        <div className="detail-times">
          <div>started {fmtTime(call.started_at)}</div>
          {call.ended_at && <div>ended {fmtTime(call.ended_at)}</div>}
        </div>
      </header>

      <CallModelStack snapshot={call.model_snapshot} />

      {call.recording_url && (
        <div id="outbound-recording" className="recording">
          <div className="section-label">RECORDING</div>
          <audio id="outbound-recording-audio" controls src={call.recording_url} preload="none" />
        </div>
      )}

      <nav id="outbound-detail-tabs" className="detail-tabs">
        <button
          id="outbound-tab-summary"
          className={tab === "summary" ? "is-active" : ""}
          onClick={() => setTab("summary")}
        >
          Summary
        </button>
        <button
          id="outbound-tab-transcript"
          className={tab === "transcript" ? "is-active" : ""}
          onClick={() => setTab("transcript")}
        >
          Transcript ({transcripts.length})
        </button>
        <button
          id="outbound-tab-tools"
          className={tab === "tools" ? "is-active" : ""}
          onClick={() => setTab("tools")}
        >
          Tool calls ({toolCalls.length})
        </button>
      </nav>

      {/* All tab panes always rendered; CSS hides non-active onscreen
          and @media print un-hides for full-record printing. */}
      <div className={`tab-pane ${tab === "summary" ? "" : "is-hidden"}`}>
        <CallSummary
          call={call}
          callId={callId}
          counts={{ transcripts: transcripts.length, toolCalls: toolCalls.length }}
        />
      </div>

      <div className={`tab-pane ${tab === "transcript" ? "" : "is-hidden"}`}>
        <div id="outbound-transcript-pane" className="transcript-pane">
          {transcripts.length === 0 && <div className="muted">No transcript yet.</div>}
          {transcripts.map((t) => (
            <div key={t.id} id={`outbound-transcript-row-${t.id}`} className={`turn turn-${t.role}`}>
              <div className="turn-meta">
                {t.role} · {fmtTime(t.ts)}
              </div>
              <div className={`bubble bubble-${t.role}`}>{t.text}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={`tab-pane ${tab === "tools" ? "" : "is-hidden"}`}>
        <div id="outbound-tools-pane" className="tools-pane">
          {toolCalls.length === 0 && <div className="muted">No tool calls yet.</div>}
          {toolCalls.map((tc) => (
            <div key={tc.id} id={`outbound-tool-row-${tc.id}`} className={`tool-row tool-${tc.status}`}>
              <div className="tool-head">
                <strong>{tc.name}</strong>
                <span className={`tool-status status-${tc.status}`}>{tc.status}</span>
                {tc.duration_ms !== null && (
                  <span className="tool-dur">{tc.duration_ms} ms</span>
                )}
                <span className="tool-time">{fmtTime(tc.ts)}</span>
              </div>
              {tc.arguments && (
                <pre className="tool-json">args: {prettyJson(tc.arguments)}</pre>
              )}
              {tc.result && (
                <pre className="tool-json">result: {prettyJson(tc.result)}</pre>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

